<template>
  <div
    class="flex flex-col gap-y-4"
    data-cypress="cat-overview-page"
  >
    <kudo-card>
      <kudo-block>
        <kudo-form-control
          :helper-text="typeHelperText"
          :errors="$vuelidateErrorsToStrings(typeErrors)"
        >
          <kudo-radio-group
            v-model="type"
            label-text="Who declared the CAT?"
            :radio-options="tagTypeOptions"
            :show-radio-control="false"
            :has-border="true"
            :radio-list-classes="[
              'grid gap-4 text-center grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
            ]"
            :disabled="isEditMode"
            required
            @update:model-value="clearPcsNumber"
          />
        </kudo-form-control>
        <kudo-expand-transition v-if="shouldShowPcsNumberInput">
          <kudo-form-control
            class="sm:w-72 border-l-2 border-plum pl-4"
            :errors="$vuelidateErrorsToStrings(pcsNumberErrors)"
            :helper-text="numberHelperText"
          >
            <p class="font-medium my-4 text-sm">
              What is the CAT Number?
              <span class="text-red">*</span>
            </p>
            <kudo-input
              v-model="pcsNumber"
              :disabled="isEditMode"
              label-text="CAT number"
              data-cypress="cat-overview-pcs-number-input"
              @input="debouncePcsNumberCheck"
            />
          </kudo-form-control>
        </kudo-expand-transition>
      </kudo-block>
    </kudo-card>
    <kudo-card>
      <kudo-block>
        <p class="font-medium mb-4 text-sm">
          What is the CAT description?
          <span class="text-red">*</span>
        </p>
        <kudo-form-control
          class="sm:w-72"
          :errors="$vuelidateErrorsToStrings(descriptionErrors)"
          :helper-text="descriptionHelperText"
        >
          <kudo-input
            v-model="description"
            :disabled="isEditMode"
            :aria-invalid="descriptionErrors.length"
            label-text="Details"
            placeholder="E.g. Wind & Hail"
            aria-placeholder="E.g. Wind & Hail"
            :maxlength="18"
            data-cypress="cat-overview-description-input"
          >
            <template #label-utility>
              <div class="flex justify-end grow text-gray-dark text-normal">
                {{ description.length }} / 18
              </div>
            </template>
          </kudo-input>
        </kudo-form-control>
      </kudo-block>
    </kudo-card>
  </div>
</template>

<script setup>
import { KudoFormControl, KudoCard, KudoBlock, KudoRadioGroup, KudoExpandTransition, KudoInput } from '@openly-engineering/kudo'
import { OVERVIEW_STEP_PATH, tagTypeOptions, pcsTypeOption, EDIT_MODE } from './cat-tag-utils'
import useCatTagValidation from '~/composables/useCatTagValidation'

const { $store } = useNuxtApp()
const { getFieldErrors, checkPcsNumberIsUnique } = useCatTagValidation()

const isEditMode = computed(() => $store.state.adminClaims.mode === EDIT_MODE)
const typeHelperText = computed(() => isEditMode.value ? 'This selection cannot be changed.' : 'This selection cannot be changed later.')
const numberHelperText = computed(() => isEditMode.value ? 'This number cannot be changed.' : 'This number cannot be changed later.')
const descriptionHelperText = computed(() => isEditMode.value ? 'This description cannot be changed.' : 'This description cannot be changed later.')

const type = computed({
  get() { return $store.state.adminClaims[OVERVIEW_STEP_PATH].formData.type },
  set(val) { $store.commit('adminClaims/setTagType', val) },
})

const pcsNumber = computed({
  get() { return $store.state.adminClaims[OVERVIEW_STEP_PATH].formData.pcsNumber },
  set(val) { $store.commit('adminClaims/setPcsNumber', val) },
})

const description = computed({
  get() { return $store.state.adminClaims[OVERVIEW_STEP_PATH].formData.description },
  set(val) { $store.commit('adminClaims/setDescription', val) },
})

const shouldShowPcsNumberInput = computed(() => $store.getters['adminClaims/shouldShowPcsNumber'])
const typeErrors = computed(() => getFieldErrors(OVERVIEW_STEP_PATH, 'type'))
const pcsNumberErrors = computed(() => getFieldErrors(OVERVIEW_STEP_PATH, 'pcsNumber'))
const descriptionErrors = computed(() => getFieldErrors(OVERVIEW_STEP_PATH, 'description'))

const clearPcsNumber = (typeValue) => {
  if (typeValue !== pcsTypeOption.value) {
    $store.commit('adminClaims/setPcsNumber', '')
  }
}

let timer
function debouncePcsNumberCheck() {
  clearTimeout(timer)
  timer = setTimeout(checkPcsNumberIsUnique, 800)
}

</script>
